import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import BulkImportModal from '../components/BulkImportModal';
import { EQUIPMENT_IMPORT_SCHEMA } from '../components/importSchemas';

import ControlledDraftJson from '../components/ControlledDraftJson';

const API_BASE = process.env.REACT_APP_API_URL;
const STATUS_COLORS = { Draft:'#94a3b8','In Review':'#60a5fa',Released:'#46BB89',Superseded:'#f59e0b',Obsolete:'#EC2124' };

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status]||'#666';
  return <span style={{ display:'inline-block', padding:'2px 10px', borderRadius:20, fontSize:11,
    fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', background:c+'22', color:c }}>{status}</span>;
}
function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}

export default function EquipmentList() {
  const { getToken } = useAuth(); const { user } = useUser(); const navigate = useNavigate();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canEdit = ['admin','quality-manager','production-engineer','service'].includes(role) || ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(String(role||'').replace(/^test-/,''));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status:'Released', type:'' });
  const [cloning, setCloning] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (filters.status) params.set('status',filters.status);
      if (filters.type)   params.set('type',filters.type);
      const res = await window.fetch(`${API_BASE}/quality/equipment?${params}`, {
        headers:{ Authorization:`Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) setItems(data.items||[]);
    } catch {} finally { setLoading(false); }
  }, [getToken,filters]);

  useEffect(()=>{ load(); },[load]);
  const setFilter=(k,v)=>setFilters(f=>({...f,[k]:v}));

  const clone = async (e, item) => {
    e.stopPropagation();
    setCloning(item._id);
    try {
      const token = await getToken();
      const [detailRes, docNumRes] = await Promise.all([
        window.fetch(`${API_BASE}/quality/equipment/${item._id}`, { headers:{ Authorization:`Bearer ${token}` } }),
        window.fetch(`${API_BASE}/quality/next-doc-number?prefix=EQ-NIN-`, { headers:{ Authorization:`Bearer ${token}` } }),
      ]);
      const detail = await detailRes.json();
      const docNum = await docNumRes.json();
      const { _id, documentNumber, createdAt, updatedAt, revisionHistory, __v, ...rest } = detail.item;
      const createRes = await window.fetch(`${API_BASE}/quality/equipment`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ ...rest, documentNumber:docNum.documentNumber, name:`${rest.name} (Copy)`, currentStatus:'Draft', currentRevision:'A', revisionHistory:[] }),
      });
      const created = await createRes.json();
      if (created.ok) navigate(`/quality/equipment/${created.item._id}`);
    } catch {} finally { setCloning(null); }
  };
  const fs={ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'6px 10px',fontSize:13 };

  return (
    <div>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20 }}>
        <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>Equipment Registry</h2>
        {canEdit && (
          <div style={{ display:'flex',gap:10 }}>
            <button onClick={()=>setShowImport(true)}
              style={{ background:'transparent',color:'#f59e0b',border:'1px solid #f59e0b66',borderRadius:6,
                padding:'8px 16px',fontWeight:700,fontSize:13,cursor:'pointer' }}>↥ Bulk import</button>
            <button onClick={()=>navigate('/quality/equipment/new')}
              style={{ background:'#f59e0b',color:'#000',border:'none',borderRadius:6,
                padding:'8px 18px',fontWeight:700,fontSize:13,cursor:'pointer' }}>+ New Equipment</button>
          </div>
        )}
      </div>

      <ControlledDraftJson library="equipment" onApplied={load} />

      <div style={{ display:'flex',gap:10,marginBottom:16,flexWrap:'wrap' }}>
        {canEdit && (
          <select value={filters.status} onChange={e=>setFilter('status',e.target.value)} style={fs}>
            <option value="">All statuses</option>
            {['Draft','In Review','Released','Superseded','Obsolete'].map(s=><option key={s}>{s}</option>)}
          </select>
        )}
        <select value={filters.type} onChange={e=>setFilter('type',e.target.value)} style={fs}>
          <option value="">All types</option>
          {['Test Bench','Measurement Instrument','Programming Station','Computer / Workstation',
            'Soldering Station','Label Printer','Barcode Scanner','Camera / Vision System',
            'Power Supply','Climate Chamber','Conveyor / Transport','Hand Tool','Generic']
            .map(t=><option key={t}>{t}</option>)}
        </select>
      </div>

      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,overflow:'hidden' }}>
        {loading ? <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>Loading…</div>
        : items.length===0 ? (
          <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>
            No equipment found.{' '}
            {canEdit && <button onClick={()=>navigate('/quality/equipment/new')}
              style={{ background:'none',border:'none',color:'#f59e0b',cursor:'pointer',fontSize:14 }}>Register one.</button>}
          </div>
        ) : (
          <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
            <thead>
              <tr style={{ color:'var(--ni-text-muted)',fontSize:11,textTransform:'uppercase',
                letterSpacing:'0.06em',background:'rgba(0,0,0,0.2)' }}>
                {['Doc No.','Name','Type','Manufacturer','Model','Rev.','Status','Updated'].map(h=>(
                  <th key={h} style={{ padding:'10px 16px',textAlign:'left',fontWeight:600,whiteSpace:'nowrap' }}>{h}</th>
                ))}
                {canEdit && <th style={{ padding:'10px 16px',textAlign:'left',fontWeight:600 }}></th>}
              </tr>
            </thead>
            <tbody>
              {items.map(item=>(
                <tr key={item._id} onClick={()=>navigate(`/quality/equipment/${item._id}`)}
                  style={{ cursor:'pointer',borderTop:'1px solid var(--ni-border-soft)' }}
                  onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.03)'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{ padding:'11px 16px',fontFamily:'monospace',fontSize:12,color:'#f59e0b',whiteSpace:'nowrap' }}>{item.documentNumber}</td>
                  <td style={{ padding:'11px 16px',fontWeight:600 }}>{item.name}</td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)' }}>{item.type}</td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)' }}>{item.manufacturer||'—'}</td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)' }}>{item.model||'—'}</td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)',fontFamily:'monospace' }}>{item.currentRevision}</td>
                  <td style={{ padding:'11px 16px',whiteSpace:'nowrap' }}><StatusBadge status={item.currentStatus}/></td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>
                    {item.updatedAt?new Date(item.updatedAt).toLocaleDateString():'—'}
                  </td>
                  {canEdit && (
                    <td style={{ padding:'6px 16px' }} onClick={e=>e.stopPropagation()}>
                      <button onClick={e=>clone(e, item)} disabled={cloning===item._id}
                        style={{ background:'none', border:'1px solid #f59e0b44', color:'#f59e0b', borderRadius:4,
                          padding:'3px 10px', fontSize:11, cursor:'pointer', whiteSpace:'nowrap', opacity:cloning===item._id?0.5:1 }}>
                        {cloning===item._id ? '…' : 'Copy'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop:10,color:'var(--ni-text-muted)',fontSize:12 }}>{items.length} item{items.length!==1?'s':''}</div>

      {showImport && (
        <BulkImportModal
          schema={EQUIPMENT_IMPORT_SCHEMA}
          endpoint="/quality/equipment/import"
          accent="#f59e0b"
          title="Bulk import equipment"
          getToken={getToken}
          onClose={()=>{ setShowImport(false); load(); }}
          onDone={()=>setFilters(f=>({ ...f, status:'Draft' }))}
        />
      )}
    </div>
  );
}
